import React, { useEffect, useReducer, useState } from 'react';

import {
    GetContractTermsInput,
    GetContractsByIdInput,
    GetDefaultContentAcquisitionEntityInput,
    GetPaperPlanMetadataInput,
    GetPlayTypesInput,
    GetProgramTypesInput,
    GetRatchetModelsByIdInput,
    GetRoyaltyModelTypesInput,
    GetServiceLaunchDateInput,
    PaperPlanMetadata,
    PlayType,
    ProgramType,
    RatchetModels
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { isEmpty, isNil, isUndefined } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useHistory, useLocation, useParams } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BreadcrumbWrapper } from 'src/components/Breadcrumb';
import { LoadingSkeleton } from 'src/components/LoadingSkeleton';
import { RateCardHeader } from 'src/components/RateCardHeader';
import { StepTracker } from 'src/components/StepTracker';
import { ViewPrimeAMFRateCard } from 'src/components/ViewPrimeAMFRateCard';
import { ContractBreadcrumbTitle, DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { useDynamicRedirect } from 'src/hooks';
import { useTitle } from 'src/hooks/useTitle';
import { IMetadata } from 'src/types/IMetadata';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues, IRateCardDetailParams } from 'src/types/IRateCard';
import { IStepTrackerStep } from 'src/types/IStepTrackerStep';
import { IToast } from 'src/types/IToaster';
import { getRateCardBreadcrumbItems } from 'src/util/RateCards';
import { SaveManager } from 'src/util/SaveManager/SaveManager';
import { getCurrentYearMonth } from 'src/util/dateTimeUtil';
import { buildDefaultCaeMap, buildServiceLaunchDateMap } from 'src/util/metadataUtil';

import { ConfirmationModal } from '../ConfirmationModal';
import { mapPrimeAMFContractToRateCard } from '../ViewPrimeAMFRateCard/mapPrimeAMFContractToRateCard';
import { AddContractTerms } from './AddContractTerms';
import { generateRatchetModelsWithThresholds } from './AddContractTerms/AddConversionModel/conversionModelThresholds';
import { AddCoreRates } from './AddCoreRates';
import './AddPrimeAMFRateCard.scss';
import { PrimeAMFRateCardSaveProps, primeAMFRateCardFullStepList, primeAMFRateCardHandlers } from './Handlers/Handlers';
import { DEFAULT_PRIME_AMF_RATE_CARD_ERRORS, rateCardValidationReducer } from './rateCardValidationReducer';
import { rateCardValuesReducer } from './rateCardValuesReducer';

interface IAddPrimeAMFRateCardProps {
    serviceId: number;
}

export const AddPrimeAMFRateCard = ({ serviceId }: IAddPrimeAMFRateCardProps) => {
    const history = useHistory();
    const { pathname } = useLocation();
    const params: IRateCardDetailParams = useParams();
    const rateCardId = parseInt(params.rateCardId);
    const [isEditDraft] = React.useState<boolean>(!isNil(rateCardId) && pathname.includes(ROUTES.RATE_CARDS.ADD));
    const [rateCardValues, dispatchRateCardValues] = useReducer(rateCardValuesReducer, {} as IPrimeAMFRateCardValues);
    const [rateCardErrors, dispatchRateCardErrors] = useReducer(
        rateCardValidationReducer,
        DEFAULT_PRIME_AMF_RATE_CARD_ERRORS
    );
    const [dataLoaded, setDataLoaded] = React.useState<boolean>(false);
    const [playTypes, setPlayTypes] = React.useState<PlayType[]>([]);
    const [customerTypes, setCustomerTypes] = React.useState<PaperPlanMetadata[]>([]);
    const [programTypes, setProgramTypes] = React.useState<ProgramType[]>([]);
    const [conversionModels, setConversionModels] = useState<RatchetModels>([]);

    const { setTopAlertMsg, userInfo, setToasts, metadata } = React.useContext(AppContext);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isSaveUpdatesModalOpen, setIsSaveUpdatesModalOpen] = useState(false);
    const [saveUpdatesComments, setSaveUpdatesComments] = useState('');
    const [augmentedMetadata, setAugmentedMetadata] = useState<IMetadata>(metadata);

    const basePath = `${ROUTES.RATE_CARDS.INDEX}/${params.editType}/${rateCardId}`;

    const detailPath = `${ROUTES.RATE_CARDS.INDEX}/${rateCardId}`;
    const coreRatesDraftPath = `${ROUTES.RATE_CARDS.INDEX}${ROUTES.RATE_CARDS.ADD}/${rateCardId}${ROUTES.RATE_CARDS.STEP.CORE_RATES}`;

    const steps: IStepTrackerStep[] = [
        {
            stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS,
            stepTitle: 'Contract terms',
            StepComponent: AddContractTerms
        },
        {
            stepId: ROUTES.RATE_CARDS.STEP.CORE_RATES,
            stepTitle: 'Core rates',
            StepComponent: AddCoreRates
        },
        {
            stepId: ROUTES.RATE_CARDS.STEP.REVIEW,
            stepTitle: 'Review and publish',
            StepComponent: ViewPrimeAMFRateCard
        }
    ];

    useDynamicRedirect(basePath, steps[0].stepId);

    const step = `/${pathname.split('/').pop()}`;
    const currentStepIndex = steps.findIndex(({ stepId }) => stepId === step);

    const editStepName = steps.find((stepData) => stepData.stepId === step)?.stepTitle ?? 'rate card';
    useTitle(`${isEditDraft ? 'Add' : 'Edit'} ${editStepName}: ${rateCardValues.contractName}`);

    useEffect(() => {
        const contractPromise = contractConfigurationClient
            .getContractsById(
                GetContractsByIdInput.clone({
                    contractId: rateCardId,
                    excludeRoyaltyModelTypes: true,
                    royaltyModelCodes: ['MAU', 'CPP'],
                    includeDraft: true,
                    includeIneligibleContractDeals: true
                })
            )
            .toPromise();

        const contractTermsPromise = contractConfigurationClient
            .getContractTerms(GetContractTermsInput.clone({ contractId: rateCardId }))
            .toPromise();

        const playTypesPromise = contractConfigurationClient.getPlayTypes(GetPlayTypesInput.clone({})).toPromise();

        const customerTypesPromise = contractConfigurationClient
            .getPaperPlanMetadata(GetPaperPlanMetadataInput.clone({ serviceIds: [serviceId] }))
            .toPromise();

        const programTypesPromise = contractConfigurationClient
            .getProgramTypes(GetProgramTypesInput.clone({}))
            .toPromise();

        // Assuming that we are always editing ratchet model definitions for the current or the future months,
        // This logic needs to change if that is not the case. Jira for evaluating if that is a valid use case
        // https://jira.music.amazon.dev/browse/RAD-3467
        const currentYearMonth = getCurrentYearMonth();
        const ratchetModelsPromise = contractConfigurationClient
            .getRatchetModelsById(
                GetRatchetModelsByIdInput.clone({
                    serviceId: serviceId,
                    month: currentYearMonth
                })
            )
            .toPromise();

        // Augment metadata object
        const caePromise = contractConfigurationClient
            .getDefaultContentAcquisitionEntity(
                GetDefaultContentAcquisitionEntityInput.clone({ includeUnlaunchedTerritories: true })
            )
            .toPromise();
        const serviceLaunchDatesPromise = contractConfigurationClient
            .getServiceLaunchDate(GetServiceLaunchDateInput.clone({}))
            .toPromise();
        const royaltyModelTypesPromise = contractConfigurationClient
            .getRoyaltyModelTypes(GetRoyaltyModelTypesInput.clone({}))
            .toPromise();

        Promise.all([
            contractPromise,
            contractTermsPromise,
            playTypesPromise,
            customerTypesPromise,
            programTypesPromise,
            ratchetModelsPromise,
            caePromise,
            serviceLaunchDatesPromise,
            royaltyModelTypesPromise
        ])
            .then((response) => {
                const [
                    contractResponse,
                    contractTermsResponse,
                    playTypesResponse,
                    customerTypesResponse,
                    programTypesResponse,
                    ratchetModelsResponse,
                    caeResponse,
                    serviceLaunchDatesResponse,
                    royaltyModelTypesResponse
                ] = response;
                if (contractResponse.contracts.length > 0) {
                    const mappedRateCardValues = mapPrimeAMFContractToRateCard(contractResponse, contractTermsResponse);
                    dispatchRateCardValues({ type: 'set_rate_card_values', rateCardValues: mappedRateCardValues });
                    setPlayTypes(
                        // filter out empty playTypeDefinition object inside playType
                        playTypesResponse.playTypes?.map((playType) => {
                            const playTypeDefinitions = playType.playTypeDefinitions?.filter(
                                (playTypeDefinition) => !isUndefined(playTypeDefinition.playTypeDefinitionId)
                            );
                            return {
                                ...playType,
                                playTypeDefinitions
                            };
                        }) ?? []
                    );
                    setCustomerTypes(customerTypesResponse.planMetadata ?? []);
                    setProgramTypes(programTypesResponse.programTypes ?? []);
                    setConversionModels(generateRatchetModelsWithThresholds(ratchetModelsResponse.ratchetModels ?? []));

                    setAugmentedMetadata((prevMetadata) => ({
                        ...prevMetadata,
                        CaeMapping: buildDefaultCaeMap(caeResponse.defaultContentAcquisitionEntities),
                        activeServiceLaunchDateMapping: buildServiceLaunchDateMap(
                            serviceLaunchDatesResponse.serviceLaunches ?? []
                        ),
                        royaltyModelTypes: royaltyModelTypesResponse.royaltyModelTypes ?? []
                    }));
                }
            })
            .catch((error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error('Contract APIs Unexpected error', error);
                setTopAlertMsg(error.receivedException.message);
            })
            .finally(() => setDataLoaded(true));
    }, []);

    const saveProps: PrimeAMFRateCardSaveProps = {
        contractId: rateCardId,
        user: userInfo.username,
        values: rateCardValues,
        metadata: augmentedMetadata,
        customerTypes: customerTypes,
        programTypes: programTypes,
        conversionModels: conversionModels
    };

    const { saveChanges, registerChange, isSaving } = SaveManager.trackRateCardState(
        saveProps,
        primeAMFRateCardHandlers
    );

    const setWorkflowStepValues = (stepId: string, action: IPrimeAMFRateCardValuesAction) => {
        registerChange(stepId);
        dispatchRateCardValues(action);
    };

    const handleToasts = (path: string) => {
        if (isEditDraft) {
            const toast: IToast =
                path === detailPath
                    ? {
                          id: 'published-rate-card-toast',
                          timeout: DEFAULT_TOASTER_TIMEOUT,
                          message: 'New rate card published',
                          type: 'success'
                      }
                    : {
                          id: 'saved-rate-card-draft-toast',
                          timeout: DEFAULT_TOASTER_TIMEOUT,
                          message: 'Saved rate card as draft',
                          type: 'success'
                      };
            setToasts(path !== coreRatesDraftPath ? [toast] : []);
        } else {
            const toast: IToast = {
                id: 'rate-card-updated-toast',
                timeout: DEFAULT_TOASTER_TIMEOUT,
                message: 'Rate card updated',
                type: 'success'
            };

            setToasts(path === detailPath ? [toast] : []);
        }
    };

    const navigateOnSuccess = (path: string) => {
        return (errors: string[]) => {
            if (errors.length == 0) {
                handleToasts(path);
                history.push(path);
            } else {
                setTopAlertMsg(errors.join('\n'));
            }
        };
    };

    const saveIfDraftOrReview = async (steps?: string[], isPublish?: boolean) => {
        if (isEditDraft) {
            return await saveChanges(steps, isPublish, primeAMFRateCardFullStepList);
        } else if (step === ROUTES.RATE_CARDS.STEP.REVIEW) {
            return await saveChanges(steps, isPublish, primeAMFRateCardFullStepList);
        }
        return [];
    };

    const saveThenNavigate = (path: string) => {
        saveIfDraftOrReview([step], false).then(navigateOnSuccess(path));
    };

    const saveAllThenNavigate = (path: string) => {
        saveIfDraftOrReview([step], isEditDraft).then(navigateOnSuccess(path));
    };

    const onNext = () => {
        saveThenNavigate(`${basePath}${steps[currentStepIndex + 1].stepId}`);
    };

    const onPrevious = () => {
        history.push(`${basePath}${steps[currentStepIndex - 1].stepId}`);
    };

    const onCancel = () => setIsCancelModalOpen(true);

    const onSaveDraft = () => {
        saveThenNavigate(`${basePath}${steps[currentStepIndex].stepId}`);
    };

    const onPublish = () => {
        if (isEditDraft) {
            saveAllThenNavigate(detailPath);
        } else {
            setIsSaveUpdatesModalOpen(true);
        }
    };

    return (
        <Column alignmentHorizontal="center" height="100%" spacingInset="200" data-testid="prime-amf-rate-card-loader">
            {isSaving ? (
                <Row width="100%" height="100vh" alignmentHorizontal="center">
                    <Column alignmentHorizontal="center" alignmentVertical="center">
                        <Loader size="large" />
                        <Text type="b200" color="secondary">
                            Saving contract changes...
                        </Text>
                    </Column>
                </Row>
            ) : dataLoaded ? (
                isEmpty(rateCardValues) ? (
                    <Text type="h300">Rate card not found</Text>
                ) : (
                    <Column width="100%" height="100%" spacing="400" heights={['fit', 'fit', 'fill']}>
                        <BreadcrumbWrapper
                            breadcrumbItems={getRateCardBreadcrumbItems(
                                isEditDraft
                                    ? ContractBreadcrumbTitle.AddNewContract
                                    : ContractBreadcrumbTitle.EditContract
                            )}
                        />
                        <RateCardHeader rateCardHeaderValues={rateCardValues}></RateCardHeader>
                        <Row
                            alignmentVertical="stretch"
                            wrap="down"
                            widths={['fit', 'fill']}
                            spacingInset="400 none none none"
                        >
                            <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                                <StepTracker steps={steps} basePath={basePath} currentStepIndex={currentStepIndex} />
                            </Column>
                            <Column className="step-component" spacing="500" heights={['fill', 'fit']}>
                                <Switch>
                                    {steps.map(({ stepId, stepTitle, StepComponent }) => (
                                        <Route key={stepId} path={`${basePath}${stepId}`}>
                                            <StepComponent
                                                rateCardValues={rateCardValues}
                                                rateCardValuesAdd={rateCardValues}
                                                serviceId={serviceId}
                                                rateCardId={rateCardId}
                                                dispatchRateCardValues={(action: IPrimeAMFRateCardValuesAction) =>
                                                    setWorkflowStepValues(stepId, action)
                                                }
                                                rateCardErrors={rateCardErrors}
                                                dispatchRateCardErrors={dispatchRateCardErrors}
                                                playTypes={playTypes}
                                                customerTypes={customerTypes}
                                                programTypes={programTypes}
                                                conversionModels={conversionModels}
                                                stepTitle={stepTitle}
                                                augmentedMetadata={augmentedMetadata}
                                                dataLoaded={dataLoaded}
                                            />
                                        </Route>
                                    ))}
                                </Switch>
                                <Row alignmentHorizontal="end">
                                    <Button type="link" onClick={onCancel}>
                                        Cancel
                                    </Button>
                                    {isEditDraft && currentStepIndex !== 0 && (
                                        <Button
                                            type="tertiary"
                                            onClick={onSaveDraft}
                                            disabled={rateCardErrors.isNextDisabled || !dataLoaded}
                                        >
                                            Save
                                        </Button>
                                    )}
                                    {currentStepIndex > 0 && (
                                        <Button type="tertiary" onClick={onPrevious} disabled={!dataLoaded}>
                                            Previous
                                        </Button>
                                    )}
                                    {currentStepIndex < steps.length - 1 && (
                                        <Button
                                            type="primary"
                                            onClick={onNext}
                                            disabled={rateCardErrors.isNextDisabled || !dataLoaded}
                                        >
                                            Next
                                        </Button>
                                    )}
                                    {currentStepIndex === steps.length - 1 && (
                                        <Button type="primary" onClick={onPublish} disabled={!dataLoaded}>
                                            {isEditDraft ? 'Publish' : 'Save updates'}
                                        </Button>
                                    )}
                                </Row>
                            </Column>
                        </Row>
                        <ConfirmationModal
                            isOpen={isCancelModalOpen}
                            onClose={() => setIsCancelModalOpen(false)}
                            title={`Cancel ${!isEditDraft ? 'edit' : 'add'} rate card`}
                            body="Are you sure you want to cancel? Any changes will be lost."
                            primaryButtonText="Yes, cancel"
                            primaryButtonOnClick={() => history.push(`${ROUTES.RATE_CARDS.INDEX}/${rateCardId}`)}
                            secondaryButtonText="No, go back"
                            secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
                        />
                        <ConfirmationModal
                            isOpen={isSaveUpdatesModalOpen}
                            onClose={() => setIsSaveUpdatesModalOpen(false)}
                            title={`Save updates`}
                            body="Are you sure you want save updates to this rate card?"
                            warning="Updates may require reruns for past accounting periods."
                            showWarning={true}
                            comments={saveUpdatesComments}
                            onCommentsChange={setSaveUpdatesComments}
                            showComments={true}
                            primaryButtonText="Yes, save updates"
                            primaryButtonOnClick={() => saveAllThenNavigate(detailPath)}
                            secondaryButtonText="Cancel"
                            secondaryButtonOnClick={() => setIsSaveUpdatesModalOpen(false)}
                        />
                    </Column>
                )
            ) : (
                <Column width="100%" height="100%" spacing="400" heights={['fit', 'fit', 'fill']}>
                    <BreadcrumbWrapper
                        breadcrumbItems={getRateCardBreadcrumbItems(
                            isEditDraft ? ContractBreadcrumbTitle.AddNewContract : ContractBreadcrumbTitle.EditContract
                        )}
                    />
                    <Box type="fill" spacingInset="400">
                        <Column spacing="200">
                            <Skeleton count={5} />
                        </Column>
                    </Box>
                    <Row
                        alignmentVertical="stretch"
                        wrap="down"
                        widths={['fit', 'fill']}
                        spacingInset="400 none none none"
                    >
                        <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                            <StepTracker steps={steps} basePath={basePath} currentStepIndex={currentStepIndex} />
                        </Column>
                        <Column className="step-component" spacing="500" heights={['fill', 'fit']}>
                            <LoadingSkeleton />
                        </Column>
                    </Row>
                </Column>
            )}
        </Column>
    );
};
